<template>
  <div class="container-fluid py-4">
    <!-- Page Header -->
    <div class="row mb-4">
      <div class="col">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h1 class="h3 mb-1">
              <i class="bi bi-plus-circle me-2"></i>
              Nuevo Script de Monitoreo
            </h1>
            <p class="text-muted mb-0">
              Configura y ejecuta un script de feed de datos en segundo plano
            </p>
          </div>
          <router-link
            to="/scripts"
            class="btn btn-outline-secondary"
          >
            <i class="bi bi-arrow-left me-1"></i>
            Volver
          </router-link>
        </div>
      </div>
    </div>

    <!-- Create Form -->
    <div class="row">
      <div class="col-lg-8">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">Configuración del Script</h5>
          </div>
          <div class="card-body">
            <form @submit.prevent="handleSubmit">
              <!-- Client Selection -->
              <div class="mb-4">
                <h6 class="mb-3">
                  <i class="bi bi-person-circle me-2"></i>
                  Cliente
                </h6>

                <div class="mb-3">
                  <label for="client" class="form-label">
                    Cliente <span class="text-danger">*</span>
                  </label>
                  <select
                    id="client"
                    v-model="formData.client_id"
                    class="form-select"
                    :class="{'is-invalid': errors.client_id}"
                    required
                    @change="onClientChange"
                  >
                    <option :value="null">Seleccionar cliente...</option>
                    <option
                      v-for="client in clientsWithCredentials"
                      :key="client.id"
                      :value="client.id"
                    >
                      {{ client.nombre }} - {{ client.nombre_base_datos }}
                    </option>
                  </select>
                  <div v-if="errors.client_id" class="invalid-feedback">
                    {{ errors.client_id }}
                  </div>
                  <div class="form-text">
                    Solo se muestran clientes con credenciales MyGeotab configuradas
                  </div>
                </div>

                <!-- Client info -->
                <div v-if="selectedClient" class="alert alert-info">
                  <strong><i class="bi bi-info-circle me-1"></i> Cliente seleccionado:</strong><br>
                  <div class="mt-2">
                    <small>
                      <strong>Nombre:</strong> {{ selectedClient.nombre }}<br>
                      <strong>Base de datos:</strong> {{ selectedClient.nombre_base_datos }}<br>
                      <strong>Usuario:</strong> {{ selectedClient.usuario_base_datos }}
                    </small>
                  </div>
                </div>
              </div>

              <!-- Script-specific Credentials (Optional) -->
              <div class="mb-4">
                <h6 class="mb-3">
                  <i class="bi bi-key me-2"></i>
                  Credenciales Específicas del Script (Opcional)
                </h6>

                <div class="alert alert-info small">
                  <i class="bi bi-info-circle me-1"></i>
                  Si especificas credenciales aquí, se usarán en lugar de las credenciales del cliente.
                  Esto es útil cuando necesitas usar diferentes usuarios de MyGeotab para diferentes scripts del mismo cliente.
                </div>

                <div class="row g-3">
                  <div class="col-md-6">
                    <label for="script_user" class="form-label">
                      Usuario MyGeotab
                    </label>
                    <input
                      id="script_user"
                      v-model="formData.user"
                      type="text"
                      class="form-control"
                      placeholder="usuario@empresa.com"
                    >
                    <div class="form-text">
                      Dejar vacío para usar el usuario del cliente
                    </div>
                  </div>

                  <div class="col-md-6">
                    <label for="script_password" class="form-label">
                      Contraseña MyGeotab
                    </label>
                    <input
                      id="script_password"
                      v-model="formData.password"
                      type="password"
                      class="form-control"
                      placeholder="••••••••"
                    >
                    <div class="form-text">
                      Dejar vacío para usar la contraseña del cliente
                    </div>
                  </div>
                </div>
              </div>

              <!-- Script Configuration -->
              <div class="mb-4">
                <h6 class="mb-3">
                  <i class="bi bi-gear me-2"></i>
                  Configuración del Script
                </h6>

                <div class="row g-3">
                  <!-- Feeder Type -->
                  <div class="col-12">
                    <label for="feeder_type" class="form-label">
                      Tipo de Feeder <span class="text-danger">*</span>
                    </label>
                    <select
                      id="feeder_type"
                      v-model="formData.config.feeder_type"
                      class="form-select"
                      :class="{'is-invalid': errors.feeder_type}"
                      required
                    >
                      <option :value="null">Seleccionar tipo de feeder...</option>
                      <option value="localizarte">Localizarte</option>
                      <option value="koandina">Koandina</option>
                      <option value="linkcargas">LinkCargas</option>
                      <option value="quadminds">QuadMinds</option>
                      <option value="pae">PAE</option>
                    </select>
                    <div v-if="errors.feeder_type" class="invalid-feedback">
                      {{ errors.feeder_type }}
                    </div>
                    <div class="form-text">
                      Selecciona el sistema de destino para los datos de rastreo
                    </div>
                  </div>

                  <!-- Interval -->
                  <div class="col-md-6">
                    <label for="interval" class="form-label">
                      Intervalo de Polling (segundos) <span class="text-danger">*</span>
                    </label>
                    <input
                      id="interval"
                      v-model.number="formData.config.interval"
                      type="number"
                      class="form-control"
                      min="2"
                      max="300"
                      required
                    >
                    <div class="form-text">
                      Entre 2 y 300 segundos. Recomendado: 10 segundos
                    </div>
                  </div>

                  <!-- Single Device -->
                  <div class="col-md-6">
                    <label for="single_device" class="form-label">
                      Dispositivo Específico (Opcional)
                    </label>
                    <input
                      id="single_device"
                      v-model="formData.config.single_device"
                      type="text"
                      class="form-control"
                      placeholder="Ej: b1234"
                    >
                    <div class="form-text">
                      Dejar vacío para monitorear todos los dispositivos
                    </div>
                  </div>

                  <!-- Dry Run -->
                  <div class="col-md-6">
                    <div class="form-check form-switch">
                      <input
                        id="dryrun"
                        v-model="formData.config.dryrun"
                        type="checkbox"
                        class="form-check-input"
                        role="switch"
                      >
                      <label for="dryrun" class="form-check-label">
                        Modo Prueba (Dry Run)
                      </label>
                    </div>
                    <div class="form-text">
                      No envía datos reales, solo simula el proceso
                    </div>
                  </div>

                  <!-- Server URL -->
                  <div class="col-12">
                    <label for="server" class="form-label">
                      URL del Servidor (Opcional)
                    </label>
                    <input
                      id="server"
                      v-model="formData.config.server"
                      type="url"
                      class="form-control"
                      placeholder="https://api.example.com/endpoint"
                    >
                    <div class="form-text">
                      URL personalizada para envío de datos. Dejar vacío para usar configuración por defecto
                    </div>
                  </div>

                  <!-- Start Date -->
                  <div class="col-md-6">
                    <label for="start_date" class="form-label">
                      Fecha de Inicio (Opcional)
                    </label>
                    <input
                      id="start_date"
                      v-model="formData.config.start_date"
                      type="datetime-local"
                      class="form-control"
                    >
                    <div class="form-text">
                      Para reproducir datos históricos desde una fecha específica
                    </div>
                  </div>
                </div>
              </div>

              <!-- Notes -->
              <div class="mb-4">
                <h6 class="mb-3">
                  <i class="bi bi-sticky me-2"></i>
                  Notas Adicionales
                </h6>

                <div class="mb-3">
                  <label for="notes" class="form-label">
                    Notas (Opcional)
                  </label>
                  <textarea
                    id="notes"
                    v-model="formData.notes"
                    class="form-control"
                    rows="3"
                    placeholder="Descripción o notas sobre este script..."
                  ></textarea>
                </div>
              </div>

              <!-- Submit Buttons -->
              <div class="d-flex justify-content-end gap-2">
                <router-link
                  to="/scripts"
                  class="btn btn-outline-secondary"
                  :class="{'disabled': submitting}"
                >
                  Cancelar
                </router-link>
                <button
                  type="submit"
                  class="btn btn-primary"
                  :disabled="submitting || !formData.client_id || !formData.config.feeder_type"
                >
                  <span v-if="submitting">
                    <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    Creando...
                  </span>
                  <span v-else>
                    <i class="bi bi-play-circle me-1"></i>
                    Crear y Ejecutar
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Help Sidebar -->
      <div class="col-lg-4">
        <div class="card bg-light">
          <div class="card-header bg-primary text-white">
            <h6 class="mb-0">
              <i class="bi bi-info-circle me-1"></i>
              Ayuda
            </h6>
          </div>
          <div class="card-body">
            <h6>¿Qué hace este script?</h6>
            <p class="small">
              El script de feed de datos monitorea continuamente la ubicación de los vehículos
              desde MyGeotab y retransmite esta información a un endpoint externo.
            </p>

            <h6 class="mt-3">Parámetros importantes:</h6>
            <ul class="small">
              <li>
                <strong>Tipo de Feeder:</strong> Determina el sistema de destino al que se
                enviarán los datos. Localizarte para integración satelital o PAE para sistema genérico.
              </li>
              <li>
                <strong>Intervalo:</strong> Frecuencia de consulta a MyGeotab. Un intervalo
                menor significa actualizaciones más frecuentes pero mayor carga en el servidor.
              </li>
              <li>
                <strong>Dispositivo específico:</strong> Permite monitorear solo un vehículo
                en lugar de toda la flota.
              </li>
              <li>
                <strong>Modo prueba:</strong> Útil para verificar la configuración sin enviar
                datos reales.
              </li>
            </ul>

            <h6 class="mt-3">Requisitos:</h6>
            <ul class="small">
              <li>El cliente debe tener credenciales MyGeotab configuradas</li>
              <li>Las credenciales deben ser válidas y tener permisos adecuados</li>
              <li>El servidor debe tener acceso a internet para conectarse a MyGeotab</li>
            </ul>

            <div class="alert alert-warning mt-3 small">
              <i class="bi bi-exclamation-triangle me-1"></i>
              <strong>Nota:</strong> El script se ejecutará en segundo plano y continuará
              hasta que lo detengas manualmente.
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useScriptStore } from '../../stores/scripts'
import { useClientStore } from '../../stores/clients'

const router = useRouter()
const route = useRoute()
const scriptStore = useScriptStore()
const clientStore = useClientStore()

// State
const submitting = ref(false)
const errors = ref({})

const formData = ref({
  client_id: null,
  script_type: 'data_feed',
  user: null,
  password: null,
  config: {
    feeder_type: null,
    interval: 10,
    dryrun: false,
    single_device: null,
    server: null,
    start_date: null
  },
  notes: ''
})

// Computed
const clients = computed(() => clientStore.clients)
const clientsWithCredentials = computed(() => {
  return clients.value.filter(client =>
    client.nombre_base_datos &&
    client.usuario_base_datos &&
    client.clave_base_datos
  )
})

const selectedClient = computed(() => {
  if (!formData.value.client_id) return null
  return clients.value.find(c => c.id === formData.value.client_id)
})

// Methods
const onClientChange = () => {
  errors.value.client_id = null
}

const validateForm = () => {
  errors.value = {}

  if (!formData.value.client_id) {
    errors.value.client_id = 'Debe seleccionar un cliente'
    return false
  }

  if (!formData.value.config.feeder_type) {
    errors.value.feeder_type = 'Debe seleccionar un tipo de feeder'
    return false
  }

  if (formData.value.config.interval < 2 || formData.value.config.interval > 300) {
    errors.value.interval = 'El intervalo debe estar entre 2 y 300 segundos'
    return false
  }

  return true
}

const handleSubmit = async () => {
  if (!validateForm()) {
    return
  }

  submitting.value = true

  try {
    // Clean up config - remove empty values
    const config = { ...formData.value.config }
    if (!config.single_device) delete config.single_device
    if (!config.server) delete config.server
    if (!config.start_date) delete config.start_date

    const scriptData = {
      client_id: formData.value.client_id,
      script_type: formData.value.script_type,
      user: formData.value.user || null,
      password: formData.value.password || null,
      config: config,
      notes: formData.value.notes || null
    }

    await scriptStore.createScript(scriptData)

    // Redirect to scripts list
    router.push('/scripts')
  } catch (error) {
    console.error('Error creating script:', error)
    // Error is handled by the store and notification
  } finally {
    submitting.value = false
  }
}

// Lifecycle
onMounted(async () => {
  // Load all clients
  await clientStore.fetchClients(1, 1000)

  // Pre-select client from query parameter if present
  const clientIdFromQuery = route.query.client_id
  if (clientIdFromQuery) {
    const clientId = parseInt(clientIdFromQuery)
    if (clientsWithCredentials.value.find(c => c.id === clientId)) {
      formData.value.client_id = clientId
    }
  }

  // Check if we have any clients with credentials
  if (clientsWithCredentials.value.length === 0) {
    alert('No hay clientes con credenciales MyGeotab configuradas. Por favor, configura las credenciales en la sección de Clientes antes de crear un script.')
  }
})
</script>

<style scoped>
.form-check-input:checked {
  background-color: var(--bs-primary);
  border-color: var(--bs-primary);
}
</style>
